'use client';

import * as React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { buildDraft, FIELD_ICONS, FIELDS, initBool, initCollapsed, shallowEqualDraft, type FieldKey, type ProjectResponse } from '@/types';
import {
  Edit,
  Target,
  Check,
  X,
  Loader2,
  ChevronDown,
} from 'lucide-react';
import { RichTextEditor } from '@/components/rich-text';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { useUpdateInternalProjectInfosMutation, useGetCurrentUserQuery } from '@/state';
import { useAddPublicImageResourceMutation } from '@/state/user/client-api';
import { useTenant } from '@/hooks';
import { useClientPermissions } from '@/hooks/use-client-permissions';
import { ReadOnlyContentDisplay } from '@/components/read-only-content-display';
import { useSearchParams } from 'react-router-dom';


export function ContentDisplay({
  html,
  placeholder,
}: {
  html: string | any
  placeholder: string
}) {
  const safeHtml = typeof html === 'string' ? html : (html ? JSON.stringify(html) : '');
  
  if (!safeHtml.trim()) {
    return (
      <p className="text-sm italic text-muted-foreground/60">{placeholder}</p>
    )
  }

  return (
    <ReadOnlyContentDisplay
      html={safeHtml}
    />
  )
}
export const InternalProjectInfos = ({
  project,
}: {
  project: ProjectResponse;
}) => {
  const [draft, setDraft] = React.useState<Record<FieldKey, string>>(() =>
    buildDraft(project)
  );
  const [saving, setSaving] = React.useState<Record<FieldKey, boolean>>(() =>
    initBool()
  );
  const [editing, setEditing] = React.useState<Record<FieldKey, boolean>>(() =>
    initBool()
  );
  const [collapsed, setCollapsed] = React.useState<Record<FieldKey, boolean>>(
    () => initCollapsed()
  );
  const [updateProjectField] = useUpdateInternalProjectInfosMutation();
  const { tenant } = useTenant();
  const { isStudent, isCompany } = useClientPermissions();
  const { data: currentUser } = useGetCurrentUserQuery({ tenant: tenant! });
  const [searchParams] = useSearchParams();
  const projectType = React.useMemo(() => searchParams.get('type') ?? 'INTERNAL', [searchParams]);
  const isExternalProject = projectType === 'EXTERNAL' || project?.project_type === 2;

  const isMentor = React.useMemo(() => {
    return (
      currentUser?.email &&
      project?.project_mentor &&
      currentUser.email.toLowerCase() === project.project_mentor.toLowerCase()
    );
  }, [currentUser?.email, project?.project_mentor]);

  // Non-company users can never edit external project information
  const isProjectMember = project.members?.some((m) => m.candidate === currentUser?.id) || (project).public_project_candidates?.some((pc) => pc.candidate.id === currentUser?.id);
  const canEdit = isExternalProject ? isCompany : ((!isStudent || isMentor) && isProjectMember);

  React.useEffect(() => {
    const nextDraft = buildDraft(project);

    setDraft((prev) => (shallowEqualDraft(prev, nextDraft) ? prev : nextDraft));

    setEditing((prev) =>
      Object.values(prev).some(Boolean) ? initBool() : prev
    );
    setSaving((prev) =>
      Object.values(prev).some(Boolean) ? initBool() : prev
    );
  }, [project]);

  const handleSave = React.useCallback(
    async (field: FieldKey, options?: { keepEditing?: boolean }) => {
      try {
        setSaving((p) => ({ ...p, [field]: true }));
        const latestHtml = htmlGettersRef.current?.[field]?.() ?? draft[field];
        // Ensure we persist most recent editor state (favor latestHtml if available)
        await updateProjectField({
          tenant: tenant!,
          // For external: send to create_projects using internal project_id if available,
          // otherwise use project.id (which IS the create_projects ID for company fetches)
          projectId: isExternalProject && project.project_id ? project.project_id : project.id,
          // Always set resourceId for external so the mutation routes to UPDATE_EXTERNAL_PROJECT
          resourceId: isExternalProject ? (project.project_id ? project.id : -1) : undefined,
          data: {
            [field]: latestHtml,
          },
        }).unwrap();
        // Update local draft with canonical saved html
        setDraft((p) => ({ ...p, [field]: latestHtml }))
        toast.success('Updated successfully');
        if (!options?.keepEditing) {
          setEditing((p) => ({ ...p, [field]: false }));
        }
      } catch {
        toast.error('Failed to update');
      } finally {
        setSaving((p) => ({ ...p, [field]: false }));
      }
    },
    [project.id, draft, tenant]
  );

  const handleCancel = React.useCallback(
    (field: FieldKey) => {
      setDraft((p) => ({ ...p, [field]: (project as any)[field] ?? '' }));
      setEditing((p) => ({ ...p, [field]: false }));
    },
    [project]
  );

  const handleEdit = React.useCallback((field: FieldKey) => {
    setEditing((p) => ({ ...p, [field]: true }));
    setCollapsed((p) => ({ ...p, [field]: false }));
  }, []);

  const toggleCollapse = React.useCallback((field: FieldKey) => {
    setCollapsed((p) => ({ ...p, [field]: !p[field] }));
  }, []);

  const expandAll = () =>
    setCollapsed({
      description: false,
      objectives: false,
      expected_outcomes: false,
      scope_of_work: false,
      expected_deliverables: false,
      methodology: false,
    });
  const collapseAll = () => setCollapsed(initCollapsed());

  const [addPublicImageResource] = useAddPublicImageResourceMutation();

  const htmlGettersRef = React.useRef<Record<FieldKey, () => string | undefined> | null>(null);

  const handleUploadFile = React.useCallback(
    async (file: File): Promise<string> => {
      try {
        const formData = new FormData();
        formData.append('image_path', file);
        const data = await addPublicImageResource({ data: formData }).unwrap();
        const src = data?.image_path || data?.url || data?.file;
        if (src) return src;
        throw new Error('No image path returned from upload');
      } catch (error) {
        console.error('File upload error:', error);
        toast.error(error instanceof Error ? error.message : 'Failed to upload the file or image.');
        // Fallback to local URL
        return URL.createObjectURL(file);
      }
    },
    [addPublicImageResource]
  );

  const getSafeString = (val: any) => typeof val === 'string' ? val : (val ? JSON.stringify(val) : '');
  const filledCount = FIELDS.filter(({ field }) => getSafeString(draft[field]).trim()).length;

  return (
    <Card className="overflow-hidden border-border/40 bg-card shadow-lg p-0">
      {/* Header */}
      <div className="border-b border-border/40 bg-gradient-to-r from-muted/60 via-muted/40 to-transparent px-4 py-4 sm:px-6 sm:py-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 ring-1 ring-primary/20 sm:h-11 sm:w-11">
              <Target className="h-5 w-5 text-primary sm:h-5.5 sm:w-5.5" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground sm:text-xl">
                Project Information
              </h2>
              <p className="text-xs text-muted-foreground sm:text-sm">
                {filledCount} of {FIELDS.length} sections completed
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={expandAll}
              className="h-8 gap-1.5 text-xs bg-transparent"
            >
              Expand All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={collapseAll}
              className="h-8 gap-1.5 text-xs bg-transparent"
            >
              Collapse All
            </Button>
          </div>
        </div>
      </div>

      <CardContent className="p-0">
        <div className="divide-y divide-border/30">
          {FIELDS.map(({ label, field, placeholder, description }) => {
            const rawHtml = draft[field];
            const html = typeof rawHtml === 'string' ? rawHtml : (rawHtml ? JSON.stringify(rawHtml) : '');
            const isEditing = editing[field];
            const isSaving = saving[field];
            const isCollapsed = collapsed[field];
            const hasContent = html?.trim();
            const Icon = FIELD_ICONS[field];

            return (
              <Collapsible
                key={field}
                open={!isCollapsed}
                onOpenChange={() => !isEditing && toggleCollapse(field)}
              >
                <div
                  className={cn(
                    'transition-colors duration-150',
                    isEditing && 'bg-primary/[0.02]'
                  )}
                >
                  {/* Section Header */}
                  <div className="flex items-center gap-3 px-4 py-3 sm:px-6 sm:py-4">
                    <CollapsibleTrigger
                      className={cn(
                        'flex flex-1 items-center gap-3 text-left transition-opacity',
                        isEditing && 'pointer-events-none'
                      )}
                      disabled={isEditing}
                    >
                      <div
                        className={cn(
                          'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
                          hasContent
                            ? 'bg-primary/10 text-primary'
                            : 'bg-muted text-muted-foreground'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground sm:text-base">
                            {label}
                          </p>
                          {hasContent && (
                            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
                              Filled
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs text-muted-foreground sm:text-sm">
                          {description}
                        </p>
                      </div>

                      <ChevronDown
                        className={cn(
                          'h-5 w-5 shrink-0 text-muted-foreground transition-transform duration-200',
                          isCollapsed && 'rotate-180'
                        )}
                      />
                    </CollapsibleTrigger>

                    <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
                      {isEditing ? (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="h-8 gap-1.5 px-2 text-muted-foreground hover:text-foreground sm:px-3"
                            onClick={() => handleCancel(field)}
                            disabled={isSaving}
                          >
                            <X className="h-4 w-4" />
                            <span className="hidden sm:inline">Cancel</span>
                          </Button>

                          <div className="flex gap-2 items-center">
                            <Button
                              type="button"
                              size="sm"
                              className="h-8 gap-1.5 px-2 sm:px-3"
                              onClick={() => handleSave(field)}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <>
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span className="hidden sm:inline">Saving</span>
                                </>
                              ) : (
                                <>
                                  <Check className="h-4 w-4" />
                                  <span className="hidden sm:inline">Save</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </>                      ) : (
                        canEdit && (
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 gap-1.5 px-2 bg-transparent sm:px-3"
                            onClick={() => handleEdit(field)}
                            disabled={isSaving}
                          >
                            <Edit className="h-4 w-4" />
                            <span className="hidden sm:inline">Edit</span>
                          </Button>
                        )
                      )}
                    </div>
                  </div>

                  {/* Content */}
                  <CollapsibleContent>
                    <div className="px-4 pb-4 sm:px-6 sm:pb-5">
                      {isEditing ? (
                        <RichTextEditor
                          value={html}
                          onChange={(next) => {
                            setDraft((p) => ({ ...p, [field]: next }))
                          }
                          }
                          placeholder={placeholder}
                          minHeight={200}
                          disabled={isSaving}
                          onUploadFile={handleUploadFile}
                          onProvideGetHtml={(getHtml) => {
                            if (!htmlGettersRef.current) htmlGettersRef.current = {} as Record<FieldKey, () => string | undefined>;
                            htmlGettersRef.current[field] = getHtml
                          }}
                        />
                      ) : (
                        <div
                          className={cn(
                            'min-h-[80px] rounded-xl border border-border/40 bg-muted/30 px-4 py-3 transition-all sm:px-5 sm:py-4',
                            hasContent && canEdit && 'hover:bg-muted/40 hover:border-border/60 cursor-text select-text'
                          )}
                          onDoubleClick={() => canEdit && handleEdit(field)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={() => { }}
                        >
                          <ContentDisplay
                            html={html}
                            placeholder={placeholder}
                          />
                        </div>
                      )}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
